import { registerBundle } from '@/lib/i18n';

/**
 * The `tryon` i18n namespace (BP-21 §6).
 *
 * i18next and not a plain object, unlike `features/ar`: that one is two
 * staff screens read by 26AD people in English, and this one is the client's
 * own screen on her own phone. BP-19 rule 1 then applies — a locale with no
 * entry falls back to English — so shipping `en` alone here is a normal
 * state of the build, not a gap. The three other locales are a translation
 * pass, not a code change.
 *
 * **Rule 2's two lines are not optional and not conditional.** The
 * "indicative" footer and T12's "not affiliated" line are on every render,
 * under every shade, in kiosk mode too. A disclaimer a screen can be in a
 * state without is a disclaimer somebody will eventually find the state for.
 */
export const TRYON_NAMESPACE = 'tryon';

export const tryOnStrings = {
  title: 'Try a colour on',
  lead: 'Point the camera at your hand and tap a shade.',
  kioskLead: 'Rest your hand on the stand and tap a shade.',

  camera: {
    prompt: 'Tap below and allow the camera to start.',
    granted: 'Starting the camera…',
    denied: 'The camera is turned off for 26AD. You can allow it in your phone settings.',
    unsupported: 'This device has no camera 26AD can use.',
    unavailable: 'The camera could not start. Close anything else using it and try again.',
    start: 'Start',
    live: 'Camera on',
  },

  shades: {
    heading: 'In stock today',
    // Rule 1: a shade she cannot be sold today is not offered at all, so the
    // empty state is about the shelf and not about the search.
    none: 'There are no shades to try just now. The salon will have more in soon.',
    indicative: 'Close match',
    indicativeHint:
      'This colour was read from a photograph rather than measured, so it is close rather than exact.',
    tried: 'Tried',
  },

  compare: {
    heading: 'Compare',
    hint: 'Pick up to four. A fifth replaces the one you picked first.',
    empty: 'Nothing picked yet.',
    remove: 'Remove {{shade}}',
    full: 'Four is the most that fit side by side.',
  },

  book: {
    action: 'Book this look',
    hint: 'We will put {{shade}} on your booking so the salon has it ready.',
  },

  slow: {
    title: 'This phone is struggling',
    // Rule 5. BP-22 is the still-photo render; until it ships this says what
    // is happening rather than pretending the overlay is fine.
    body: 'The live view cannot keep up on this device. Use a photograph instead — the picture is better anyway.',
  },

  // T7. §14 item 2 is applied: one stand, one lamp, and the copy says so
  // rather than implying the calibration survives somebody moving it.
  calibrate: {
    heading: 'Light check',
    hint: 'Hold the grey card on the stand and tap. Do this again if the lamp is moved.',
    action: 'Check the light',
    before: 'What the camera sees',
    after: 'Corrected',
    done: 'The light is set.',
    too_dark: 'That was too dark to read. Turn the lamp on and try again.',
    too_bright: 'That was too bright to read. Move the lamp back and try again.',
    noSample: 'The camera has to be running first.',
  },

  // BP-22 §6. The photo tab is the same screen rule 5 already points at
  // when the live view cannot keep up.
  photo: {
    tab: 'Use a photo',
    liveTab: 'Use the camera',
    hint: 'Pick a photograph of your hand. We make the picture and you can share it.',
    choose: 'Choose a photograph',
    working: 'Making your picture…',
    ready: 'Here it is.',
    share: 'Share',
    again: 'Try another',
    // Rule 1, said plainly, because it is a promise and she should be able
    // to read it before she decides.
    temporary: 'Your photograph is used for this picture only and is deleted within a day.',
    failed: {
      no_model: 'This salon has not set up photo try-on yet. The live camera still works.',
      unreadable: 'That file could not be opened. A JPEG or PNG from your gallery works best.',
      too_large: 'That photograph is too large. Try one straight from your camera roll.',
      no_hand: 'We could not find a hand in that photograph. Try one with your fingers spread.',
      internal: 'Something went wrong making that picture. Please try again.',
    },
    expired: 'That picture has expired. Make another whenever you like.',
  },

  designs: {
    heading: 'Nail art',
    none: 'No designs just now.',
    price: '₹{{amount}} · {{minutes}} min',
    clear: 'No design',
  },

  // Rule 2, on every render.
  disclaimer: {
    colour: 'Colour shown is indicative. Screens and lighting vary.',
    // T12. Plain-text brand names only, and no implication of a tie-up.
    affiliation: '26AD is not affiliated with any of the brands shown.',
    onDevice: 'The camera stays on your phone. Nothing is recorded and no picture is sent.',
  },
} as const;

/** Add the bundle to the shared instance. Idempotent. */
export function registerTryOnStrings(): void {
  registerBundle(TRYON_NAMESPACE, { en: tryOnStrings });
}

registerTryOnStrings();
